<template>
  <div class="data-table-condensed">
    <q-table
      :title="title"
      class="data-table-condensed__table scroll"
      title-class="data-table-condensed__table--title"
      dense
      virtual-scroll
      flat
      :columns="columns"
      :pagination.sync="pagination"
      :data="rows"
      no-data-label="No Tracked Players"
      color="primary"
      style="height: 300px"
      :loading="visible"
      row-key="PlayerID"
      selection="single"
      :selected.sync="selectedRow"
    >
      <template v-slot:loading>
        <q-inner-loading showing color="secondary" />
      </template>
      <template v-slot:top-right>
        <q-input
          label="Track Player ID"
          dense
          outlined
          rounded
          debounce="300"
          color="secondary"
          v-model="playerId"
          type="number"
        >
          <template v-slot:after>
            <q-btn
              :disable="!playerId"
              round
              dense
              unelevated
              icon="add"
              color="secondary"
              @click="addTrackedPlayer(playerId)"
            />
          </template>
        </q-input>
      </template>
      <template v-slot:header="props">
        <q-tr :props="props">
          <q-th v-for="col in props.cols" :key="col.name" :props="props">
            {{ col.label }}
          </q-th>
          <q-th auto-width />
        </q-tr>
      </template>

      <template v-slot:body="props">
        <q-tr
          class="cursor-pointer"
          :props="props"
          @click.native="
            (props.selected = !props.selected), getRowPlayerId(props.row)
          "
        >
          <q-td v-for="col in props.cols" :key="col.name" :props="props">
            {{ col.value }}
          </q-td>
          <q-td auto-width>
            <q-btn
              size="sm"
              color="grey"
              round
              flat
              dense
              @click="removeTrackedPlayer(props.row.PlayerID)"
              icon="delete"
            />
          </q-td>
        </q-tr>
      </template>
    </q-table>
    <Alert :open.sync="alert" :title="this.alertTitle" :body="this.alertBody" />
  </div>
</template>

<script>
import Alert from "@/components/dialog/Alert.vue";
import { formatCurrency } from "@/utils/formatCurrency";
import { formatTextLabel } from "@/utils/formatText";

export default {
  props: {
    title: String,
    dataset: String,
    timer: Number,
  },
  data() {
    return {
      selectedRow: [],
      alert: false,
      alertTitle: null,
      alertBody: null,
      playerId: null,
      userId: this.$store.getters.getUserId,
      pagination: {
        page: 1,
        rowsPerPage: 10,
      },
      columns: [],
      keys: [],
      rows: [],
      errors: [],
      visible: false,
    };
  },
  components: {
    Alert,
  },
  computed: {
    numberOfPages() {
      return Math.ceil(this.rows.length / this.pagination.rowsPerPage);
    },
  },
  methods: {
    // Fetch players tracked if any
    async fetchTrackedPlayers(userId) {
      // Dataset name is the store procedure name used to return the results.
      const payload = {
        filterOptions: {
          userId,
        },
        report: "track-player",
        dataset: this.$props.dataset.replace(/ /g, ""),
      };

      await this.$store
        .dispatch("updatePlayerTracker", payload)
        .then((response) => {
          if (response.data[0] !== undefined) {
            const keys = Object.keys(response.data[0]);
            this.columns = keys.map((key) => ({
              name: key,
              label: formatTextLabel(key),
              field: key,
              sortable: true,
              align: "left",
              format: (value) => {
                if (
                  (key === "DayBets" && value !== null) ||
                  (key === "DayWins" && value !== null) ||
                  (key === "GGR" && value !== null) ||
                  (key === "LT Deposits" && value !== null) ||
                  (key === "LT Withdrawals" && value !== null) ||
                  (key === "LT NGR" && value !== null)
                ) {
                  return formatCurrency(value);
                } else if (value !== null) {
                  return value;
                } else {
                  return `---`;
                }
              },
            }));
          }
          this.rows = response.data;
          this.visible = false;
        })
        .catch((error) => {
          this.errors.push(error);
        });
    },

    // Add player to tracker
    async addTrackedPlayer(playerId) {
      // Check if player id already in tracked list
      let playerList = [];
      let playerIdToAdd = parseInt(playerId);
      this.rows.forEach((player) => {
        playerList.push(player.PlayerID);
      });
      // If the player id already exsits - we still have to make the call
      if (playerList.includes(playerIdToAdd)) {
        const payload = {
          filterOptions: {
            userId: this.userId,
            playerId,
            networkId: this.$store.state.userDetails.networkId,
            operatorId: this.$store.state.userDetails.operatorId,
          },
          report: "track-player",
          dataset: "Player_Tracker_Add_Player",
        };
        await this.$store
          .dispatch("addPlayerToPlayerTracker", payload)
          .then((response) => {
            this.alertTitle = "Player Already Exists";
            this.alertBody =
              "Player ID: " +
              playerId +
              " already exists in " +
              this.$store.getters.GET_CURRENT_USER +
              "'s player tracker.";
            this.alert = true;
            this.$emit("getPlayerDetails", playerId);
            this.fetchTrackedPlayers(this.userId, playerId);
            this.visible = true;
            this.playerId = null;
            this.selectedRow = response.data;
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        // Load the new player id
        const payload = {
          filterOptions: {
            userId: this.userId,
            playerId,
            networkId: this.$store.state.userDetails.networkId,
            operatorId: this.$store.state.userDetails.operatorId,
          },
          report: "track-player",
          dataset: "Player_Tracker_Add_Player",
        };
        await this.$store
          .dispatch("addPlayerToPlayerTracker", payload)
          .then((response) => {
            if (response.data.length > 0) {
              // If authorised, as in a response is returned
              this.alertTitle = "Successfully Added Player";
              this.alertBody =
                "Player ID: " +
                playerId +
                " has been added to " +
                this.$store.getters.GET_CURRENT_USER +
                "'s tracker.";
              this.alert = true;
              this.$emit("getPlayerDetails", playerId);
              this.fetchTrackedPlayers(this.userId, playerId);
              this.visible = true;
              this.playerId = null;
              this.selectedRow = response.data;
            } else {
              // If not authorised to see player data
              this.alertTitle = "Player Could Not Be Added";
              this.alertBody =
                "Player ID: " +
                playerId +
                " does not exist, can not add to " +
                this.$store.getters.GET_CURRENT_USER +
                "'s player tracker.";
              this.alert = true;
              this.playerId = null;
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },

    // Remove player from tracker
    async removeTrackedPlayer(playerId) {
      const payload = {
        filterOptions: {
          userId: this.userId,
          playerId,
        },
        report: "track-player",
        dataset: "Player_Tracker_Remove_Player",
      };
      await this.$store
        .dispatch("updatePlayerTracker", payload)
        .then(() => {
          this.alertTitle = "Successfully Removed Player";
          this.alertBody =
            "Player ID: " +
            playerId +
            " has been removed from " +
            this.$store.getters.GET_CURRENT_USER +
            "'s player tracker.";
          this.alert = true;
          this.$emit("refreshPlayerDetails", null);
          this.fetchTrackedPlayers(this.userId);
          this.visible = true;
          this.playerId = null;
        })
        .catch((error) => {
          console.log(error);
        });
    },

    // Select player
    getRowPlayerId(evt) {
      this.$emit("loadPlayerDetails", evt.PlayerID);
    },
  },
  mounted() {
    this.fetchTrackedPlayers(this.userId);
    this.visible = true;
  },
};
</script>
